<div class="comparison-dashboard">
  @if (isScopeVisibleSig()) {
    <div #surfaceContainer class="comparison-dashboard__surface card">
      <div class="comparison-dashboard__toolbar-shell">
        <div class="comparison-dashboard__header-bar">
          <div class="comparison-dashboard__header-main">
            <button type="button" class="comparison-dashboard__back-button" (click)="goToMyReports()">
              <span class="comparison-dashboard__back-icon" aria-hidden="true">←</span>
              <span>My Reports</span>
            </button>

            <div class="comparison-dashboard__header-copy">
              <h2 class="comparison-dashboard__title" title="Comparison Dashboard">Comparison Dashboard</h2>
              <div class="comparison-dashboard__toolbar-meta">
                <span class="comparison-dashboard__meta-pill comparison-dashboard__meta-pill--accent">
                  {{ scopeLabelSig() }} view
                </span>
                @if (hasLoadedOnce) {
                  <span class="comparison-dashboard__meta-pill" [title]="getDisplayedSetupSummary()">
                    {{ getDisplayedSetupSummary() }}
                  </span>
                }
              </div>
            </div>
          </div>

          @if (hasLoadedOnce) {
            <div class="comparison-dashboard__header-actions">
              <!--
                A preset is how the page is arranged and whom it is narrowed to, not the period
                it was saved in, so picking one leaves the dates alone. Default is the page as
                it ships, and is what the picker falls back to when nothing is loaded.

                The list is sized apart from the field, which is narrow enough that names came
                back as "Portal z…".
              -->
              <dx-select-box
                class="comparison-dashboard__header-preset"
                [items]="presetItemsSig()"
                displayExpr="name"
                valueExpr="id"
                [value]="activePresetIdSig()"
                [buttons]="presetEditorButtonsSig()"
                label="Presets"
                labelMode="floating"
                [openOnFieldClick]="true"
                [width]="210"
                [height]="34"
                [dropDownOptions]="{ width: 'auto', minWidth: 260, maxWidth: 420 }"
                [disabled]="isLoadingSig()"
                (onItemClick)="onPresetSelected($event.itemData?.id)"
              >
                <!--
                  Default resets the page to how the report ships; the others are the viewer's
                  own, so each carries the way to be rid of it. The action stops the click from
                  reaching the row, which would otherwise load what is being deleted.
                -->
                <div *dxTemplate="let item of 'item'" class="comparison-dashboard__preset-item">
                  <span class="comparison-dashboard__preset-item-name">{{ item.name }}</span>
                  @if (item.id === defaultPresetOptionId) {
                    <i
                      class="dx-icon dx-icon-revert comparison-dashboard__preset-item-action"
                      title="Put the page back to how the report ships"
                      (click)="$event.stopPropagation(); onPresetSelected(item.id)"
                    ></i>
                  } @else {
                    <i
                      class="dx-icon dx-icon-rename comparison-dashboard__preset-item-action"
                      title="Rename this preset"
                      (click)="$event.stopPropagation(); renamePresetFromRow(item.id)"
                    ></i>
                    <i
                      class="dx-icon dx-icon-favorites comparison-dashboard__preset-item-action"
                      [class.comparison-dashboard__preset-item-action--on]="isDefaultPreset(item.id)"
                      [title]="
                        isDefaultPreset(item.id)
                          ? 'This is what the report opens on — press to stop'
                          : 'Open the report on this preset'
                      "
                      (click)="$event.stopPropagation(); toggleDefaultPreset(item.id)"
                    ></i>
                    <i
                      class="dx-icon dx-icon-trash comparison-dashboard__preset-item-action comparison-dashboard__preset-item-action--danger"
                      title="Delete this preset"
                      (click)="$event.stopPropagation(); requestPresetDeletion(item.id)"
                    ></i>
                  }
                </div>
              </dx-select-box>
              @if (showReportFilterSig()) {
                <ag-shr-report-entity-filter
                  class="comparison-dashboard__header-filter"
                  [options]="reportFilterOptionsSig()"
                  [value]="appliedReportFilterValues"
                  [placeholder]="reportFilterPlaceholderSig()"
                  [maxDisplayedTags]="1"
                  [height]="34"
                  [disabled]="isLoadingSig()"
                  (valueChange)="onReportFilterValueChanged($event)"
                ></ag-shr-report-entity-filter>
              }
              <dx-button
                hint="Comparison setup"
                type="default"
                stylingMode="contained"
                icon="preferences"
                [height]="34"
                (onClick)="openSetupModal()"
              ></dx-button>
              <dx-button
                hint="Choose which columns every table shows"
                [type]="hiddenColumnCount > 0 ? 'default' : 'normal'"
                stylingMode="outlined"
                icon="column-chooser"
                [height]="34"
                [disabled]="isLoadingSig()"
                (onClick)="openColumnPicker()"
              ></dx-button>
              <!--
                One menu for every format, so a second one does not take another place in a full header.
                No arrow: the icon alone reads as the other header buttons do, and it still opens the menu.
              -->
              <dx-drop-down-button
                hint="Export"
                icon="export"
                stylingMode="outlined"
                displayExpr="text"
                keyExpr="id"
                [showArrowIcon]="false"
                [height]="34"
                [items]="exportMenuItemsSig()"
                [dropDownOptions]="{ width: 160 }"
                [disabled]="!comparisonDataSig() || isLoadingSig() || isExporting"
                (onItemClick)="onExportMenuItemClick($event)"
              ></dx-drop-down-button>
              <dx-button
                hint="Fullscreen"
                type="normal"
                stylingMode="outlined"
                icon="agi-up-right-and-down-left-from-center"
                [height]="34"
                [disabled]="isLoadingSig()"
                (onClick)="toggleFullScreen(surfaceContainer)"
              ></dx-button>
            </div>
          }
        </div>
      </div>

      <ag-shr-modal-window
        #presetDeleteDialogRef
        title="Delete preset"
        [width]="420"
        [height]="'auto'"
        [useScrollView]="false"
        [showFullScreenButton]="false"
        actionTitle="Delete"
        actionIcon="trash"
        [inProgress]="isDeletingPresetSig()"
        (onSaveClick)="confirmPresetDeletion()"
        (onHidden)="cancelPresetDeletion()"
      >
        <div class="comparison-dashboard__preset-dialog">
          <div class="comparison-dashboard__preset-dialog-copy">
            <p class="comparison-dashboard__preset-dialog-hint">
              "{{ presetPendingDeletionSig()?.name }}" will be gone for good.
            </p>

            <p class="comparison-dashboard__preset-dialog-hint">
              The page keeps the arrangement it is showing — it simply stops being saved under that name.
            </p>
          </div>
        </div>
      </ag-shr-modal-window>

      <ag-shr-modal-window
        #presetDialogRef
        [title]="presetDialogTitle"
        [width]="420"
        [height]="'auto'"
        [useScrollView]="false"
        [showFullScreenButton]="false"
        actionTitle="Save"
        [saveButtonDisabled]="!canConfirmPresetDialog"
        [inProgress]="isSavingPresetSig()"
        (onSaveClick)="confirmPresetDialog()"
      >
        <div class="comparison-dashboard__preset-dialog">
          <div class="comparison-dashboard__preset-dialog-copy">
            <p class="comparison-dashboard__preset-dialog-hint">
              Saves the columns, sorting, filters and table sizes on the page, together with the RMD and agency filter.
            </p>

            <p class="comparison-dashboard__preset-dialog-hint">
              The date range is not part of a preset, so this can be put on any period.
            </p>
          </div>

          <dx-text-box
            [value]="presetDraftName"
            placeholder="Preset name"
            [showClearButton]="true"
            valueChangeEvent="input"
            (onValueChanged)="presetDraftName = $event.value ?? ''"
            (onEnterKey)="confirmPresetDialog()"
          ></dx-text-box>
        </div>
      </ag-shr-modal-window>

      <dx-popup
        [(visible)]="isColumnPickerVisible"
        [showTitle]="true"
        title="Columns"
        [dragEnabled]="false"
        [hideOnOutsideClick]="true"
        [showCloseButton]="true"
        [width]="360"
        [height]="'auto'"
      >
        <div class="comparison-dashboard__column-picker">
          <p class="comparison-dashboard__column-picker-hint">
            Sets every table on the page at once. A table that does not carry a column ignores it, and each table's own
            picker can still overrule this for itself.
          </p>
          @for (column of hideableColumns; track column.field) {
            <dx-check-box
              class="comparison-dashboard__column-picker-item"
              [text]="column.caption"
              [value]="isColumnShown(column.field)"
              (onValueChanged)="onColumnVisibilityChanged(column.field, $event.value === true)"
            ></dx-check-box>
          }
          @if (hiddenColumnCount > 0) {
            <dx-button
              class="comparison-dashboard__column-picker-reset"
              text="Show all columns"
              type="normal"
              stylingMode="text"
              (onClick)="showAllColumns()"
            ></dx-button>
          }
        </div>
      </dx-popup>

      <ag-shr-modal-window
        #setupDialogRef
        title="Comparison Setup"
        [width]="760"
        [height]="'auto'"
        [useScrollView]="false"
        [showFullScreenButton]="false"
        actionTitle="APPLY SETUP"
        actionIcon="check"
        [inProgress]="isApplyingSetupSig()"
        [extraToolbarItems]="setupDialogToolbarItems"
        (onSaveClick)="applySetup()"
        (onHidden)="onSetupModalHidden()"
      >
        <div class="comparison-dashboard__modal">
          <dx-form
            #setupFormRef
            class="comparison-dashboard__modal-form"
            [formData]="draftSetup"
            [colCount]="2"
            [validationGroup]="setupValidationGroup"
            [showValidationSummary]="true"
            labelMode="floating"
            (onFieldDataChanged)="setupFormRef.instance.validate()"
          >
            <dxi-item itemType="group" caption="Comparison range" [colSpan]="2" [colCount]="2">
              <dxi-item itemType="group" [colSpan]="2" [colCount]="2">
                <dxi-item
                  dataField="mode"
                  editorType="dxSelectBox"
                  [isRequired]="true"
                  [label]="{ text: 'Mode' }"
                  [editorOptions]="{
                    items: comparisonModeOptions,
                    displayExpr: 'text',
                    valueExpr: 'id',
                    showClearButton: false,
                    onValueChanged: onDraftModeChange,
                  }"
                >
                  <dxi-validation-rule type="required" message="Mode is required"></dxi-validation-rule>
                </dxi-item>
              </dxi-item>

              @switch (draftSetup.mode) {
                @case ("ytd") {
                  <dxi-item itemType="group" [colCount]="2">
                    <dxi-item
                      dataField="currentYear"
                      editorType="dxSelectBox"
                      [isRequired]="true"
                      [label]="{ text: 'Current year' }"
                      [editorOptions]="{
                        items: currentYearOptionsSig(),
                        displayExpr: 'text',
                        valueExpr: 'id',
                        showClearButton: false,
                        onValueChanged: onDraftCurrentYearChange,
                      }"
                    >
                      <dxi-validation-rule type="required" message="Current year is required"></dxi-validation-rule>
                    </dxi-item>

                    <dxi-item
                      dataField="compareYear"
                      editorType="dxSelectBox"
                      [isRequired]="true"
                      [label]="{ text: 'Compare year' }"
                      [editorOptions]="{
                        items: compareYearOptionsSig(),
                        displayExpr: 'text',
                        valueExpr: 'id',
                        showClearButton: false,
                        onValueChanged: onDraftCompareYearChange,
                      }"
                    >
                      <dxi-validation-rule type="required" message="Compare year is required"></dxi-validation-rule>
                    </dxi-item>
                  </dxi-item>

                  <dxi-item
                    dataField="stopDate"
                    editorType="dxDateBox"
                    [isRequired]="true"
                    [label]="{ text: 'Stop date' }"
                    [editorOptions]="{
                      min: getDraftStopDateMin(),
                      max: getDraftStopDateMax(),
                      displayFormat: 'MM/dd',
                      showClearButton: false,
                      useMaskBehavior: true,
                      type: 'date',
                      onValueChanged: onDraftStopDateChange,
                    }"
                  >
                    <dxi-validation-rule type="required" message="Stop date is required"></dxi-validation-rule>
                  </dxi-item>
                }

                @case ("quarter") {
                  <dxi-item itemType="group" [colCount]="2">
                    <dxi-item
                      dataField="currentYear"
                      editorType="dxSelectBox"
                      [isRequired]="true"
                      [label]="{ text: 'Current year' }"
                      [editorOptions]="{
                        items: currentYearOptionsSig(),
                        displayExpr: 'text',
                        valueExpr: 'id',
                        showClearButton: false,
                        onValueChanged: onDraftCurrentYearChange,
                      }"
                    >
                      <dxi-validation-rule type="required" message="Current year is required"></dxi-validation-rule>
                    </dxi-item>

                    <dxi-item
                      dataField="compareYear"
                      editorType="dxSelectBox"
                      [isRequired]="true"
                      [label]="{ text: 'Compare year' }"
                      [editorOptions]="{
                        items: compareYearOptionsSig(),
                        displayExpr: 'text',
                        valueExpr: 'id',
                        showClearButton: false,
                        onValueChanged: onDraftCompareYearChange,
                      }"
                    >
                      <dxi-validation-rule type="required" message="Compare year is required"></dxi-validation-rule>
                    </dxi-item>
                  </dxi-item>

                  <dxi-item [colSpan]="2">
                    <div *dxTemplate class="comparison-dashboard__quarter-picker">
                      <div class="comparison-dashboard__quarter-list">
                        @for (quarter of quarterOptions; track quarter.id) {
                          <label class="comparison-dashboard__quarter-item">
                            <dx-check-box
                              [value]="draftSetup.quarters.includes(quarter.id)"
                              (onValueChanged)="onQuarterToggle(quarter.id, !!$event.value)"
                            ></dx-check-box>
                            <span>{{ quarter.text }}</span>
                          </label>
                        }
                      </div>
                    </div>
                  </dxi-item>
                }

                @case ("ranges") {
                  <dxi-item [colSpan]="2">
                    <div *dxTemplate class="comparison-dashboard__range-box-group">
                      <dx-date-range-box
                        class="comparison-dashboard__date-range-box"
                        [startDate]="draftSetup.currentRangeStart"
                        [endDate]="draftSetup.currentRangeEnd"
                        (startDateChange)="onDraftCurrentRangeStartInput($event)"
                        (endDateChange)="onDraftCurrentRangeEndInput($event)"
                        startDateLabel="Target Start date"
                        endDateLabel="Target End date"
                        labelMode="floating"
                        displayFormat="MM/dd/yyyy"
                        [showClearButton]="false"
                        [useMaskBehavior]="true"
                        dateSerializationFormat="yyyy-MM-dd"
                      >
                        <dx-validator [validationGroup]="setupValidationGroup">
                          <dxi-validation-rule
                            type="required"
                            message="Current range is required"
                          ></dxi-validation-rule>
                          <dxi-validation-rule
                            type="custom"
                            [validationCallback]="validateDraftCurrentRangeOrder"
                            message="Current range start cannot be after current range end"
                          ></dxi-validation-rule>
                          <dxi-validation-rule
                            type="custom"
                            [validationCallback]="validateDraftRangesDoNotOverlap"
                            message="Current and comparison ranges cannot overlap"
                          ></dxi-validation-rule>
                        </dx-validator>
                      </dx-date-range-box>
                    </div>
                  </dxi-item>

                  <dxi-item [colSpan]="2">
                    <div *dxTemplate class="comparison-dashboard__range-box-group">
                      <dx-date-range-box
                        class="comparison-dashboard__date-range-box"
                        [startDate]="draftSetup.comparisonRangeStart"
                        [endDate]="draftSetup.comparisonRangeEnd"
                        (startDateChange)="onDraftComparisonRangeStartInput($event)"
                        (endDateChange)="onDraftComparisonRangeEndInput($event)"
                        startDateLabel="Comparison Start date"
                        endDateLabel="Comparison End date"
                        labelMode="floating"
                        displayFormat="MM/dd/yyyy"
                        [showClearButton]="false"
                        [useMaskBehavior]="true"
                        dateSerializationFormat="yyyy-MM-dd"
                      >
                        <dx-validator [validationGroup]="setupValidationGroup">
                          <dxi-validation-rule
                            type="required"
                            message="Comparison range is required"
                          ></dxi-validation-rule>
                          <dxi-validation-rule
                            type="custom"
                            [validationCallback]="validateDraftComparisonRangeOrder"
                            message="Comparison range start cannot be after comparison range end"
                          ></dxi-validation-rule>
                          <dxi-validation-rule
                            type="custom"
                            [validationCallback]="validateDraftRangesDoNotOverlap"
                            message="Current and comparison ranges cannot overlap"
                          ></dxi-validation-rule>
                        </dx-validator>
                      </dx-date-range-box>
                    </div>
                  </dxi-item>
                }
              }
            </dxi-item>
          </dx-form>

        </div>
      </ag-shr-modal-window>

      <div #contentContainer class="comparison-dashboard__content">
        @if (unavailableReason) {
          <ag-shr-report-unavailable
            [reason]="unavailableReason"
            [scope]="currentScopeSig()"
          ></ag-shr-report-unavailable>
        } @else {
          <dx-scroll-view class="comparison-dashboard__scroll-view" [useNative]="false" [showScrollbar]="'always'">
            @if (loadErrorMessage) {
              <div class="comparison-dashboard__scroll-content">
                <div class="comparison-dashboard__error-state">
                  <div class="comparison-dashboard__error-eyebrow">Comparison report unavailable</div>
                  <h3 class="comparison-dashboard__section-title">We couldn't load this dashboard</h3>
                  <p class="comparison-dashboard__section-copy">{{ loadErrorMessage }}</p>
                  <dx-button
                    text="TRY AGAIN"
                    type="default"
                    stylingMode="contained"
                    [disabled]="isLoadingSig()"
                    (onClick)="retryLoad()"
                  ></dx-button>
                </div>
              </div>
            } @else {
              @if (comparisonDataSig(); as comparisonData) {
                <div class="comparison-dashboard__scroll-content">
                  @if (comparisonData.paid.canDisplayGrid) {
                    <section class="comparison-dashboard__section">
                      <div class="comparison-dashboard__section-header">
                        <div>
                          <h3 class="comparison-dashboard__section-title">Paid Production</h3>
                          <p class="comparison-dashboard__section-copy">
                            Side-by-side paid totals, movers, and trend comparison.
                          </p>
                        </div>
                      </div>

                      <div class="comparison-dashboard__kpis">
                        @for (card of getOverviewCards(comparisonData.paid); track card.label) {
                          <div class="comparison-dashboard__kpi-card">
                            <span class="comparison-dashboard__kpi-label">{{ card.label }}</span>
                            <span
                              class="comparison-dashboard__kpi-value"
                              [class.comparison-dashboard__kpi-value--positive]="card.tone === 'positive'"
                              [class.comparison-dashboard__kpi-value--negative]="card.tone === 'negative'"
                            >
                              {{ card.value }}
                            </span>
                          </div>
                        }
                      </div>

                      <ng-container
                        *ngTemplateOutlet="
                          overallComparisonGrid;
                          context: { dataset: comparisonData.paid, datasetKey: 'paid', showGoalColumns: true }
                        "
                      ></ng-container>

                      @if (showEntityMoverGrids()) {
                        <ng-container
                          *ngTemplateOutlet="
                            moverGridPairCard;
                            context: {
                              dataset: comparisonData.paid,
                              datasetKey: 'paid',
                              level: 'mga',
                            }
                          "
                        ></ng-container>
                      }

                      @if (hasAgentMoverGrids(comparisonData.paid)) {
                        <ng-container
                          *ngTemplateOutlet="
                            moverGridPairCard;
                            context: {
                              dataset: comparisonData.paid,
                              datasetKey: 'paid',
                              level: 'agent',
                            }
                          "
                        ></ng-container>
                      }

                      @if (showAllMgasProductionTable()) {
                        <ng-container
                          *ngTemplateOutlet="
                            allMgasProductionGrid;
                            context: {
                              dataset: comparisonData.paid,
                              showGoalColumns: true,
                              block: BlockDatasetOptions.paid,
                            }
                          "
                        ></ng-container>
                      }

                      @if (showAllAgentsProductionTable()) {
                        <ng-container
                          *ngTemplateOutlet="
                            allAgentsProductionGrid;
                            context: {
                              dataset: comparisonData.paid,
                              block: BlockDatasetOptions.paid,
                            }
                          "
                        ></ng-container>
                      }
                    </section>
                  } @else {
                    <section class="comparison-dashboard__section">
                      <div class="comparison-dashboard__section-header">
                        <div>
                          <h3 class="comparison-dashboard__section-title">Paid Production</h3>
                          <p class="comparison-dashboard__section-copy">
                            Side-by-side paid totals, movers, and trend comparison.
                          </p>
                        </div>
                      </div>

                      <div class="comparison-dashboard__empty-state">
                        <div class="comparison-dashboard__unavailable-card">
                          <div class="comparison-dashboard__unavailable-eyebrow">Section unavailable</div>
                          <div class="comparison-dashboard__unavailable-title">
                            No paid comparison grid for this selection
                          </div>
                          <p class="comparison-dashboard__unavailable-copy">
                            The current filters returned summary data without a comparable paid grid. Adjust the
                            selected ranges or scope if you need the detailed comparison section.
                          </p>
                        </div>
                      </div>
                    </section>
                  }

                  @if (comparisonData.submit.canDisplayGrid) {
                    <section class="comparison-dashboard__section">
                      <div class="comparison-dashboard__section-header">
                        <div>
                          <h3 class="comparison-dashboard__section-title">Submitted Production</h3>
                          <p class="comparison-dashboard__section-copy">
                            Submitted production for direct range-over-range comparison without goal-based scoring.
                          </p>
                        </div>
                      </div>

                      <div class="comparison-dashboard__kpis">
                        @for (card of getOverviewCards(comparisonData.submit, false); track card.label) {
                          <div class="comparison-dashboard__kpi-card">
                            <span class="comparison-dashboard__kpi-label">{{ card.label }}</span>
                            <span
                              class="comparison-dashboard__kpi-value"
                              [class.comparison-dashboard__kpi-value--positive]="card.tone === 'positive'"
                              [class.comparison-dashboard__kpi-value--negative]="card.tone === 'negative'"
                            >
                              {{ card.value }}
                            </span>
                          </div>
                        }
                      </div>

                      <ng-container
                        *ngTemplateOutlet="
                          overallComparisonGrid;
                          context: { dataset: comparisonData.submit, datasetKey: 'submit', showGoalColumns: false }
                        "
                      ></ng-container>

                      @if (showEntityMoverGrids()) {
                        <ng-container
                          *ngTemplateOutlet="
                            moverGridPairCard;
                            context: {
                              dataset: comparisonData.submit,
                              datasetKey: 'submit',
                              level: 'mga',
                            }
                          "
                        ></ng-container>
                      }

                      @if (hasAgentMoverGrids(comparisonData.submit)) {
                        <ng-container
                          *ngTemplateOutlet="
                            moverGridPairCard;
                            context: {
                              dataset: comparisonData.submit,
                              datasetKey: 'submit',
                              level: 'agent',
                            }
                          "
                        ></ng-container>
                      }

                      @if (showAllMgasProductionTable()) {
                        <ng-container
                          *ngTemplateOutlet="
                            allMgasProductionGrid;
                            context: {
                              dataset: comparisonData.submit,
                              showGoalColumns: false,
                              block: BlockDatasetOptions.submit,
                            }
                          "
                        ></ng-container>
                      }

                      @if (showAllAgentsProductionTable()) {
                        <ng-container
                          *ngTemplateOutlet="
                            allAgentsProductionGrid;
                            context: {
                              dataset: comparisonData.submit,
                              block: BlockDatasetOptions.submit,
                            }
                          "
                        ></ng-container>
                      }
                    </section>
                  } @else {
                    <section class="comparison-dashboard__section">
                      <div class="comparison-dashboard__section-header">
                        <div>
                          <h3 class="comparison-dashboard__section-title">Submitted Production</h3>
                          <p class="comparison-dashboard__section-copy">
                            Submitted production for direct range-over-range comparison without goal-based scoring.
                          </p>
                        </div>
                      </div>

                      <div class="comparison-dashboard__empty-state">
                        <div class="comparison-dashboard__unavailable-card">
                          <div class="comparison-dashboard__unavailable-eyebrow">Section unavailable</div>
                          <div class="comparison-dashboard__unavailable-title">
                            No submitted comparison grid for this selection
                          </div>
                          <p class="comparison-dashboard__unavailable-copy">
                            The current filters returned summary data without a comparable submitted grid. Adjust the
                            selected ranges or scope if you need the detailed comparison section.
                          </p>
                        </div>
                      </div>
                    </section>
                  }

                  @if (getAvailableChartDatasetOptions(comparisonData).length) {
                    <section class="comparison-dashboard__section comparison-dashboard__section--charts">
                      <div class="comparison-dashboard__section-header comparison-dashboard__section-header--split">
                        <div>
                          <h3 class="comparison-dashboard__section-title">Charts</h3>
                          <p class="comparison-dashboard__section-copy">
                            Trend and ranking charts use the same mock contract the backend will eventually return.
                          </p>
                        </div>

                        @if (getAvailableChartDatasetOptions(comparisonData).length > 1) {
                          <div class="comparison-dashboard__chart-picker">
                            <label class="comparison-dashboard__field-label">Chart focus</label>
                            <dx-select-box
                              [dataSource]="getAvailableChartDatasetOptions(comparisonData)"
                              displayExpr="text"
                              valueExpr="id"
                              [value]="chartDatasetKey"
                              (onValueChanged)="onChartDatasetChange($event.value)"
                            ></dx-select-box>
                          </div>
                        }
                      </div>

                      @if (getTrendDataset(comparisonData); as selectedDataset) {
                        <div class="comparison-dashboard__chart-grid">
                          <div class="comparison-dashboard__chart-card">
                            <div class="comparison-dashboard__grid-title">Premium trend</div>
                            <dx-chart [dataSource]="selectedDataset.trend" palette="Soft">
                              <dxo-common-series-settings
                                argumentField="label"
                                type="spline"
                              ></dxo-common-series-settings>
                              <dxo-tooltip [enabled]="true" [customizeTooltip]="customizeCurrencyTooltip"></dxo-tooltip>
                              <dxo-legend verticalAlignment="bottom" horizontalAlignment="center"></dxo-legend>
                              <dxo-argument-axis>
                                <dxo-grid [visible]="false"></dxo-grid>
                              </dxo-argument-axis>
                              <dxi-value-axis>
                                <dxo-label [customizeText]="customizeCurrencyAxisLabel"></dxo-label>
                              </dxi-value-axis>
                              <dxi-series
                                valueField="currentPremium"
                                [name]="getMetricCaption('current', 'Premium')"
                                color="#0f6cbd"
                              ></dxi-series>
                              <dxi-series
                                valueField="comparisonPremium"
                                [name]="getMetricCaption('comparison', 'Premium')"
                                color="#7f8c9f"
                              ></dxi-series>
                            </dx-chart>
                          </div>

                          <div class="comparison-dashboard__chart-card">
                            <div class="comparison-dashboard__grid-title">Policy trend</div>
                            <dx-chart [dataSource]="selectedDataset.trend" palette="Harmony Light">
                              <dxo-common-series-settings
                                argumentField="label"
                                type="line"
                              ></dxo-common-series-settings>
                              <dxo-tooltip [enabled]="true" [customizeTooltip]="customizeNumberTooltip"></dxo-tooltip>
                              <dxo-legend verticalAlignment="bottom" horizontalAlignment="center"></dxo-legend>
                              <dxo-argument-axis>
                                <dxo-grid [visible]="false"></dxo-grid>
                              </dxo-argument-axis>
                              <dxi-value-axis>
                                <dxo-label [customizeText]="customizeNumberAxisLabel"></dxo-label>
                              </dxi-value-axis>
                              <dxi-series
                                valueField="currentPolicies"
                                [name]="getMetricCaption('current', 'Policies')"
                                color="#198754"
                              ></dxi-series>
                              <dxi-series
                                valueField="comparisonPolicies"
                                [name]="getMetricCaption('comparison', 'Policies')"
                                color="#adb5bd"
                              ></dxi-series>
                            </dx-chart>
                          </div>

                          <div class="comparison-dashboard__chart-card comparison-dashboard__chart-card--wide">
                            <div class="comparison-dashboard__grid-title">Premium difference ranking</div>
                            <dx-chart [dataSource]="selectedDataset.ranking" [rotated]="true" palette="Material">
                              <dxo-common-series-settings
                                argumentField="entityName"
                                valueField="premiumDifference"
                                type="bar"
                              ></dxo-common-series-settings>
                              <dxo-tooltip [enabled]="true" [customizeTooltip]="customizeRankingTooltip"></dxo-tooltip>
                              <dxo-legend [visible]="false"></dxo-legend>
                              <dxi-series
                                valueField="premiumDifference"
                                name="Premium Difference"
                                color="#2d6da3"
                              ></dxi-series>
                              <dxi-value-axis>
                                <dxo-label [customizeText]="customizeCurrencyAxisLabel"></dxo-label>
                              </dxi-value-axis>
                            </dx-chart>
                          </div>
                        </div>
                      }
                    </section>
                  }
                </div>
              }
            }
          </dx-scroll-view>
        }
      </div>

      <dx-load-panel
        [container]="contentContainer"
        [position]="{ my: 'center', at: 'center', of: contentContainer }"
        [visible]="isLoadingSig()"
        [showIndicator]="true"
        [showPane]="true"
        [shading]="true"
        shadingColor="rgba(255,255,255,0.7)"
      ></dx-load-panel>
    </div>
  }

  @if (!isScopeVisibleSig() && isAccessKnownSig()) {
    <div class="comparison-dashboard__surface card">
      <div class="comparison-dashboard__toolbar-shell">
        <div class="comparison-dashboard__header-bar">
          <div class="comparison-dashboard__header-main">
            <button type="button" class="comparison-dashboard__back-button" (click)="goToMyReports()">
              <span class="comparison-dashboard__back-icon" aria-hidden="true">←</span>
              <span>My Reports</span>
            </button>
          </div>
        </div>
      </div>
      <ag-shr-report-unavailable reason="no-access" [scope]="currentScopeSig()"></ag-shr-report-unavailable>
    </div>
  }

  <!--
    Over the page rather than in place of it: the page stays mounted behind with its arrangement,
    saved or not, and closing the preview puts the viewer back on it exactly as it was.
  -->
  @if (pdfPreviewSig(); as pdfPreview) {
    <ag-portal-production-comparison-pdf-preview
      [title]="pageTitleSig()"
      [request]="pdfPreview.request"
      [blockGroups]="pdfPreview.blockGroups"
      [fileName]="pdfPreview.fileName"
      (closed)="closePdfPreview()"
    ></ag-portal-production-comparison-pdf-preview>
  }
</div>

<ng-template #allMgasProductionGrid let-dataset="dataset" let-showGoalColumns="showGoalColumns" let-block="block">
  <div
    #allMgasCard
    class="comparison-dashboard__grid-card comparison-dashboard__all-entities-card"
    [class.comparison-dashboard__grid-card--full-screen]="isCardFullScreen(getAllEntitiesGridId(block, 'mga'))"
  >
    <!--
      Hover reveals the way to put this table on the whole screen. It sits on the card's top
      edge rather than among the card's own controls, which every card already fills with
      reset, columns and download.
    -->
    @if (!isCardFullScreen(getAllEntitiesGridId(block, 'mga'))) {
      <div class="comparison-dashboard__card-expand">
        <dx-button
          class="comparison-dashboard__card-expand-button"
          icon="agi-up-right-and-down-left-from-center"
          stylingMode="text"
          hint="Full screen"
          (onClick)="toggleCardFullScreen(getAllEntitiesGridId(block, 'mga'), allMgasCard)"
        ></dx-button>
      </div>
    }
    <div class="comparison-dashboard__grid-card-header">
      <div class="comparison-dashboard__grid-title">All MGAs Production</div>
      <div class="comparison-dashboard__grid-card-actions">
        @if (isGridCustomised(getAllEntitiesGridId(block, "mga"))) {
          <dx-button
            hint="Reset this table to its default columns, sort and filters"
            type="normal"
            stylingMode="text"
            icon="revert"
            (onClick)="resetGridState(getAllEntitiesGridId(block, 'mga'))"
          ></dx-button>
        }
        <dx-button
          hint="Choose which columns this table shows"
          type="normal"
          stylingMode="text"
          icon="column-chooser"
          (onClick)="openColumnChooser(getAllEntitiesGridId(block, 'mga'))"
        ></dx-button>
        <dx-button
          hint="Download"
          type="normal"
          stylingMode="text"
          icon="xlsxfile"
          [disabled]="isExporting"
          (onClick)="exportAllMgasTable(dataset, block)"
        ></dx-button>
        @if (isCardFullScreen(getAllEntitiesGridId(block, 'mga'))) {
          <dx-button
            hint="Exit full screen"
            type="normal"
            stylingMode="text"
            icon="agi-down-left-and-up-right-to-center"
            (onClick)="toggleCardFullScreen(getAllEntitiesGridId(block, 'mga'), allMgasCard)"
          ></dx-button>
        }
      </div>
    </div>
    <div class="comparison-dashboard__grid-shell comparison-dashboard__all-entities-shell">
      <dx-data-grid
        [dataSource]="getAllEntitiesStore(block, 'mga')"
        (onInitialized)="onGridStateHostInitialized($event, getAllEntitiesGridId(block, 'mga'))"
        (onOptionChanged)="onGridStateChanged(getAllEntitiesGridId(block, 'mga'))"
        (onContentReady)="onGridStateHostReady(getAllEntitiesGridId(block, 'mga'))"
        [showBorders]="true"
        [columnAutoWidth]="true"
        [allowColumnResizing]="true"
        [wordWrapEnabled]="false"
        [hoverStateEnabled]="true"
        [height]="getCardGridHeight(getAllEntitiesGridId(block, 'mga'))"
      >
        <!--
          Remote throughout: the pager, the filter row and the header filter all ask the
          endpoint, so they act on every row rather than on the page that happens to be loaded.
        -->
        <dxo-remote-operations
          [paging]="true"
          [sorting]="true"
          [filtering]="true"
          [grouping]="true"
        ></dxo-remote-operations>
        <dxo-paging [enabled]="true" [pageSize]="allTableDefaultPageSize"></dxo-paging>
        <dxo-pager
          [visible]="true"
          displayMode="adaptive"
          [showPageSizeSelector]="true"
          [allowedPageSizes]="allTablePageSizes"
          [showInfo]="true"
          [showNavigationButtons]="true"
        ></dxo-pager>
        <dxo-filter-row [visible]="true"></dxo-filter-row>
        <dxo-header-filter [visible]="true"></dxo-header-filter>
        <dxo-sorting mode="single"></dxo-sorting>
        <dxo-scrolling mode="standard" showScrollbar="always"></dxo-scrolling>
        <dxo-column-fixing [enabled]="true"></dxo-column-fixing>
        <!--
          The picker is opened from the card header. Its own toolbar would sit between that
          header and the table to carry one button, so it is switched off here.
        -->
        <dxo-column-chooser [enabled]="true" mode="select"></dxo-column-chooser>
        <dxo-toolbar [visible]="false"></dxo-toolbar>

        <!--
          Declared from the table catalog, which the reporting API reads a stored arrangement
          against when it prints this table — so what ships here and what it assumes cannot differ.
        -->
        @for (column of getGridColumns(getAllEntitiesGridId(block, 'mga')); track column.field) {
          <dxi-column
            [dataField]="column.field"
            [caption]="column.caption"
            [width]="column.width"
            [minWidth]="column.minWidth"
            [dataType]="column.dataType"
            [format]="column.format"
            [customizeText]="column.customizeText"
            [selectedFilterOperation]="column.selectedFilterOperation"
            [sortOrder]="column.sortOrder"
            [allowHiding]="column.allowHiding"
            [allowSorting]="column.allowSorting"
            [allowFiltering]="column.allowFiltering"
            [allowHeaderFiltering]="column.allowHeaderFiltering"
            [fixed]="column.fixed"
            [fixedPosition]="column.fixedPosition"
          ></dxi-column>
        }
      </dx-data-grid>
    </div>
  </div>
</ng-template>

<ng-template #allAgentsProductionGrid let-dataset="dataset" let-block="block">
  <div
    #allAgentsCard
    class="comparison-dashboard__grid-card comparison-dashboard__all-entities-card"
    [class.comparison-dashboard__grid-card--full-screen]="isCardFullScreen(getAllEntitiesGridId(block, 'agent'))"
  >
    <!--
      Hover reveals the way to put this table on the whole screen. It sits on the card's top
      edge rather than among the card's own controls, which every card already fills with
      reset, columns and download.
    -->
    @if (!isCardFullScreen(getAllEntitiesGridId(block, 'agent'))) {
      <div class="comparison-dashboard__card-expand">
        <dx-button
          class="comparison-dashboard__card-expand-button"
          icon="agi-up-right-and-down-left-from-center"
          stylingMode="text"
          hint="Full screen"
          (onClick)="toggleCardFullScreen(getAllEntitiesGridId(block, 'agent'), allAgentsCard)"
        ></dx-button>
      </div>
    }
    <div class="comparison-dashboard__grid-card-header">
      <div class="comparison-dashboard__grid-title">All Agents Production</div>
      <div class="comparison-dashboard__grid-card-actions">
        @if (isGridCustomised(getAllEntitiesGridId(block, "agent"))) {
          <dx-button
            hint="Reset this table to its default columns, sort and filters"
            type="normal"
            stylingMode="text"
            icon="revert"
            (onClick)="resetGridState(getAllEntitiesGridId(block, 'agent'))"
          ></dx-button>
        }
        <dx-button
          hint="Choose which columns this table shows"
          type="normal"
          stylingMode="text"
          icon="column-chooser"
          (onClick)="openColumnChooser(getAllEntitiesGridId(block, 'agent'))"
        ></dx-button>
        <dx-button
          hint="Download"
          type="normal"
          stylingMode="text"
          icon="xlsxfile"
          [disabled]="isExporting"
          (onClick)="exportAllAgentsTable(dataset, block)"
        ></dx-button>
        @if (isCardFullScreen(getAllEntitiesGridId(block, 'agent'))) {
          <dx-button
            hint="Exit full screen"
            type="normal"
            stylingMode="text"
            icon="agi-down-left-and-up-right-to-center"
            (onClick)="toggleCardFullScreen(getAllEntitiesGridId(block, 'agent'), allAgentsCard)"
          ></dx-button>
        }
      </div>
    </div>
    <div class="comparison-dashboard__grid-shell comparison-dashboard__all-entities-shell">
      <dx-data-grid
        [dataSource]="getAllEntitiesStore(block, 'agent')"
        (onInitialized)="onGridStateHostInitialized($event, getAllEntitiesGridId(block, 'agent'))"
        (onOptionChanged)="onGridStateChanged(getAllEntitiesGridId(block, 'agent'))"
        (onContentReady)="onGridStateHostReady(getAllEntitiesGridId(block, 'agent'))"
        [showBorders]="true"
        [columnAutoWidth]="true"
        [allowColumnResizing]="true"
        [wordWrapEnabled]="false"
        [hoverStateEnabled]="true"
        [height]="getCardGridHeight(getAllEntitiesGridId(block, 'agent'))"
      >
        <!--
          Remote throughout: the pager, the filter row and the header filter all ask the
          endpoint, so they act on every row rather than on the page that happens to be loaded.
        -->
        <dxo-remote-operations
          [paging]="true"
          [sorting]="true"
          [filtering]="true"
          [grouping]="true"
        ></dxo-remote-operations>
        <dxo-paging [enabled]="true" [pageSize]="allTableDefaultPageSize"></dxo-paging>
        <dxo-pager
          [visible]="true"
          displayMode="adaptive"
          [showPageSizeSelector]="true"
          [allowedPageSizes]="allTablePageSizes"
          [showInfo]="true"
          [showNavigationButtons]="true"
        ></dxo-pager>
        <dxo-filter-row [visible]="true"></dxo-filter-row>
        <dxo-header-filter [visible]="true"></dxo-header-filter>
        <dxo-sorting mode="single"></dxo-sorting>
        <dxo-scrolling mode="standard" showScrollbar="always"></dxo-scrolling>
        <dxo-column-fixing [enabled]="true"></dxo-column-fixing>
        <!--
          The picker is opened from the card header. Its own toolbar would sit between that
          header and the table to carry one button, so it is switched off here.
        -->
        <dxo-column-chooser [enabled]="true" mode="select"></dxo-column-chooser>
        <dxo-toolbar [visible]="false"></dxo-toolbar>

        <!--
          Declared from the table catalog, which the reporting API reads a stored arrangement
          against when it prints this table — so what ships here and what it assumes cannot differ.
        -->
        @for (column of getGridColumns(getAllEntitiesGridId(block, 'agent')); track column.field) {
          <dxi-column
            [dataField]="column.field"
            [caption]="column.caption"
            [width]="column.width"
            [minWidth]="column.minWidth"
            [dataType]="column.dataType"
            [format]="column.format"
            [customizeText]="column.customizeText"
            [selectedFilterOperation]="column.selectedFilterOperation"
            [sortOrder]="column.sortOrder"
            [allowHiding]="column.allowHiding"
            [allowSorting]="column.allowSorting"
            [allowFiltering]="column.allowFiltering"
            [allowHeaderFiltering]="column.allowHeaderFiltering"
            [fixed]="column.fixed"
            [fixedPosition]="column.fixedPosition"
          ></dxi-column>
        }
      </dx-data-grid>
    </div>
  </div>
</ng-template>

<ng-template
  #overallComparisonGrid
  let-dataset="dataset"
  let-datasetKey="datasetKey"
  let-showGoalColumns="showGoalColumns"
>
  <div
    #overallCard
    class="comparison-dashboard__grid-card"
    [class.comparison-dashboard__grid-card--full-screen]="isCardFullScreen(getOverallGridId(datasetKey))"
  >
    <!--
      Hover reveals the way to put this table on the whole screen. It sits on the card's top
      edge rather than among the card's own controls, which are already carrying the rest.
    -->
    @if (!isCardFullScreen(getOverallGridId(datasetKey))) {
      <div class="comparison-dashboard__card-expand">
        <dx-button
          class="comparison-dashboard__card-expand-button"
          icon="agi-up-right-and-down-left-from-center"
          stylingMode="text"
          hint="Full screen"
          (onClick)="toggleCardFullScreen(getOverallGridId(datasetKey), overallCard)"
        ></dx-button>
      </div>
    }
    <div class="comparison-dashboard__grid-card-header">
      <div class="comparison-dashboard__grid-title">Overall comparison</div>
      <div class="comparison-dashboard__grid-card-actions">
        @if (isGridCustomised(getOverallGridId(datasetKey))) {
          <dx-button
            hint="Reset this table to its default columns"
            type="normal"
            stylingMode="text"
            icon="revert"
            (onClick)="resetGridState(getOverallGridId(datasetKey))"
          ></dx-button>
        }
        <dx-button
          hint="Choose which columns this table shows"
          type="normal"
          stylingMode="text"
          icon="column-chooser"
          (onClick)="openColumnChooser(getOverallGridId(datasetKey))"
        ></dx-button>
        @if (isCardFullScreen(getOverallGridId(datasetKey))) {
          <dx-button
            hint="Exit full screen"
            type="normal"
            stylingMode="text"
            icon="agi-down-left-and-up-right-to-center"
            (onClick)="toggleCardFullScreen(getOverallGridId(datasetKey), overallCard)"
          ></dx-button>
        }
      </div>
    </div>
    <div class="comparison-dashboard__grid-shell">
      <dx-data-grid
        [dataSource]="getOverallComparisonRows(dataset)"
        (onInitialized)="onGridStateHostInitialized($event, getOverallGridId(datasetKey))"
        (onOptionChanged)="onGridStateChanged(getOverallGridId(datasetKey))"
        (onContentReady)="onGridStateHostReady(getOverallGridId(datasetKey))"
        [height]="getCardGridHeight(getOverallGridId(datasetKey))"
        [showBorders]="true"
        [columnAutoWidth]="true"
        [allowColumnResizing]="true"
        [wordWrapEnabled]="false"
        [hoverStateEnabled]="true"
        [height]="'auto'"
      >
        <dxo-paging [enabled]="false"></dxo-paging>
        <dxo-sorting mode="none"></dxo-sorting>
        <!--
          The picker is opened from the card header. Its own toolbar would sit between that
          header and the table to carry one button, so it is switched off here.
        -->
        <dxo-column-chooser [enabled]="true" mode="select"></dxo-column-chooser>
        <dxo-toolbar [visible]="false"></dxo-toolbar>
        <dxo-scrolling mode="standard" showScrollbar="always"></dxo-scrolling>
        <dxo-summary
          *ngIf="hasOverallComparisonSummary(dataset)"
          [calculateCustomSummary]="getOverallComparisonSummaryCalculator(dataset)"
        >
          <dxi-total-item
            name="entityLabel"
            column="Agency Name"
            summaryType="custom"
            [customizeText]="customizeSummaryEntityText"
          ></dxi-total-item>
          @if (showGoalColumns) {
            <dxi-total-item
              name="goalAmount"
              column="goalAmount"
              summaryType="custom"
              [valueFormat]="DX_USD_CURRENCY_FORMAT"
            ></dxi-total-item>
            <dxi-total-item
              name="goalAttainedPercent"
              column="goalAttainedPercent"
              summaryType="custom"
              [customizeText]="customizePercentCellText"
            ></dxi-total-item>
          }
          <dxi-total-item
            name="currentPremium"
            column="currentPremium"
            summaryType="custom"
            [valueFormat]="DX_USD_CURRENCY_FORMAT"
          ></dxi-total-item>
          <dxi-total-item
            name="currentPolicies"
            column="currentPolicies"
            summaryType="custom"
            [valueFormat]="{ type: 'fixedPoint', precision: 1 }"
          ></dxi-total-item>
          <dxi-total-item
            name="comparisonPremium"
            column="comparisonPremium"
            summaryType="custom"
            [valueFormat]="DX_USD_CURRENCY_FORMAT"
          ></dxi-total-item>
          <dxi-total-item
            name="comparisonPolicies"
            column="comparisonPolicies"
            summaryType="custom"
            [valueFormat]="{ type: 'fixedPoint', precision: 1 }"
          ></dxi-total-item>
          <dxi-total-item
            name="premiumDifference"
            column="premiumDifference"
            summaryType="custom"
            [valueFormat]="DX_USD_CURRENCY_FORMAT"
          ></dxi-total-item>
          <dxi-total-item
            name="policyDifference"
            column="policyDifference"
            summaryType="custom"
            [valueFormat]="{ type: 'fixedPoint', precision: 1 }"
          ></dxi-total-item>
          <dxi-total-item
            name="premiumChangePercent"
            column="premiumChangePercent"
            summaryType="custom"
            [customizeText]="customizePercentCellText"
          ></dxi-total-item>
          <dxi-total-item
            name="policyChangePercent"
            column="policyChangePercent"
            summaryType="custom"
            [customizeText]="customizePercentCellText"
          ></dxi-total-item>
        </dxo-summary>

        <!--
          Declared from the table catalog, which the reporting API reads a stored arrangement
          against when it prints this table — so what ships here and what it assumes cannot differ.
        -->
        @for (column of getGridColumns(getOverallGridId(datasetKey)); track column.field) {
          <dxi-column
            [dataField]="column.field"
            [caption]="column.caption"
            [width]="column.width"
            [minWidth]="column.minWidth"
            [dataType]="column.dataType"
            [format]="column.format"
            [customizeText]="column.customizeText"
            [selectedFilterOperation]="column.selectedFilterOperation"
            [sortOrder]="column.sortOrder"
            [allowHiding]="column.allowHiding"
            [allowSorting]="column.allowSorting"
            [allowFiltering]="column.allowFiltering"
            [allowHeaderFiltering]="column.allowHeaderFiltering"
            [fixed]="column.fixed"
            [fixedPosition]="column.fixedPosition"
          ></dxi-column>
        }
      </dx-data-grid>
    </div>
  </div>
</ng-template>

<ng-template
  #moverGridPairCard
  let-dataset="dataset"
  let-datasetKey="datasetKey"
  let-level="level"
>
  <ng-container
    *ngTemplateOutlet="
      moverGridSection;
      context: {
        dataset: dataset,
        datasetKey: datasetKey,
        level: level,
        direction: 'increase',
      }
    "
  ></ng-container>

  <ng-container
    *ngTemplateOutlet="
      moverGridSection;
      context: {
        dataset: dataset,
        datasetKey: datasetKey,
        level: level,
        direction: 'decrease',
      }
    "
  ></ng-container>
</ng-template>

<!--
  One mover table, full width. Increases and decreases used to share a row, which left each
  half the page for a table that can now grow to every row the comparison has.
-->
<ng-template
  #moverGridSection
  let-dataset="dataset"
  let-datasetKey="datasetKey"
  let-level="level"
  let-direction="direction"
>
  <div
    #moverCard
    class="comparison-dashboard__grid-card"
    [class.comparison-dashboard__grid-card--full-screen]="isCardFullScreen(getMoverGridId(datasetKey, level, direction))"
    *ngIf="getMoverTableState(datasetKey, level, direction) as state"
  >
    <!--
      Hover reveals the way to put this table on the whole screen. It sits on the card's top
      edge rather than among the card's own controls, which are already carrying the rest.
    -->
    @if (!isCardFullScreen(getMoverGridId(datasetKey, level, direction))) {
      <div class="comparison-dashboard__card-expand">
        <dx-button
          class="comparison-dashboard__card-expand-button"
          icon="agi-up-right-and-down-left-from-center"
          stylingMode="text"
          hint="Full screen"
          (onClick)="toggleCardFullScreen(getMoverGridId(datasetKey, level, direction), moverCard)"
        ></dx-button>
      </div>
    }
    <!-- The same header the All MGAs card wears: title left, its controls right, on one row. -->
    <div class="comparison-dashboard__grid-card-header">
      <div class="comparison-dashboard__grid-title">{{ getMoverTableTitle(datasetKey, level, direction, state) }}</div>
      <div class="comparison-dashboard__grid-card-actions">
        <span class="comparison-dashboard__mover-count-label">Rows</span>
        <dx-select-box
          class="comparison-dashboard__mover-count-select"
          [dataSource]="topCountOptions"
          displayExpr="text"
          valueExpr="id"
          [value]="state.pageSize"
          (onValueChanged)="onMoverPageSizeChange(datasetKey, level, direction, $event.value)"
        ></dx-select-box>
        @if (isMoverCustomised(datasetKey, level, direction)) {
          <dx-button
            hint="Reset this table to its default columns, sort and filters"
            type="normal"
            stylingMode="text"
            icon="revert"
            (onClick)="resetMoverGrid(datasetKey, level, direction)"
          ></dx-button>
        }
        <dx-button
          hint="Choose which columns this table shows"
          type="normal"
          stylingMode="text"
          icon="column-chooser"
          (onClick)="openColumnChooser(getMoverGridId(datasetKey, level, direction))"
        ></dx-button>
        @if (isCardFullScreen(getMoverGridId(datasetKey, level, direction))) {
          <dx-button
            hint="Exit full screen"
            type="normal"
            stylingMode="text"
            icon="agi-down-left-and-up-right-to-center"
            (onClick)="toggleCardFullScreen(getMoverGridId(datasetKey, level, direction), moverCard)"
          ></dx-button>
        }
      </div>
    </div>

    <div class="comparison-dashboard__grid-shell">
      <ng-container
        *ngTemplateOutlet="
          moverGrid;
          context: {
            state: getMoverTableState(datasetKey, level, direction),
            datasetKey: datasetKey,
            level: level,
            direction: direction,
          }
        "
      ></ng-container>
    </div>
  </div>
</ng-template>

<ng-template
  #moverGrid
  let-state="state"
  let-datasetKey="datasetKey"
  let-level="level"
  let-direction="direction"
>
  <dx-data-grid
    class="comparison-dashboard__mover-grid"
    [dataSource]="getMoverStore(datasetKey, level, direction)"
    [showBorders]="true"
    (onInitialized)="onMoverGridInitialized($event, datasetKey, level, direction)"
    (onOptionChanged)="onGridStateChanged(getMoverGridId(datasetKey, level, direction))"
    (onContentReady)="onGridStateHostReady(getMoverGridId(datasetKey, level, direction))"
    [columnAutoWidth]="true"
    [allowColumnResizing]="true"
    [wordWrapEnabled]="false"
    [hoverStateEnabled]="true"
    [height]="getCardGridHeight(getMoverGridId(datasetKey, level, direction))"
  >
    <!--
      Remote throughout, like the full-list tables: the table holds five rows of a hundred and
      thirty, so an order or a filter it applied here would rearrange those five and leave the
      rest of the set exactly where it was.
    -->
    <dxo-remote-operations
      [paging]="true"
      [sorting]="true"
      [filtering]="true"
      [grouping]="true"
    ></dxo-remote-operations>
    <dxo-paging [enabled]="false"></dxo-paging>
    <dxo-sorting mode="single"></dxo-sorting>
    <dxo-scrolling mode="standard" showScrollbar="always"></dxo-scrolling>
    <dxo-column-fixing [enabled]="true"></dxo-column-fixing>
    <dxo-load-panel [enabled]="true" [showPane]="true" [showIndicator]="true"></dxo-load-panel>
    <!-- Numbers only: the name columns turn it off, and the server breaks every tie by name. -->
    <dxo-filter-row [visible]="true"></dxo-filter-row>
    <!--
      The name column alone carries one, and it is the column a filter row cannot serve: a name
      is picked from what is there rather than typed at. The values come from the endpoint, so
      the list is every name in the set rather than the ones on this page.
    -->
    <dxo-header-filter [visible]="true" [allowSearch]="true"></dxo-header-filter>
    <!--
      The picker is opened from the card header. Its own toolbar would sit between that
      header and the table to carry one button, so it is switched off here.
    -->
    <dxo-column-chooser [enabled]="true" mode="select"></dxo-column-chooser>
    <dxo-toolbar [visible]="false"></dxo-toolbar>

    <!--
      Declared from the table catalog, which the reporting API reads a stored arrangement
      against when it prints this table — so what ships here and what it assumes cannot differ.
    -->
    @for (column of getGridColumns(getMoverGridId(datasetKey, level, direction)); track column.field) {
      <dxi-column
        [dataField]="column.field"
        [caption]="column.caption"
        [width]="column.width"
        [minWidth]="column.minWidth"
        [dataType]="column.dataType"
        [format]="column.format"
        [customizeText]="column.customizeText"
        [selectedFilterOperation]="column.selectedFilterOperation"
        [sortOrder]="column.sortOrder"
        [allowHiding]="column.allowHiding"
        [allowSorting]="column.allowSorting"
        [allowFiltering]="column.allowFiltering"
        [allowHeaderFiltering]="column.allowHeaderFiltering"
        [fixed]="column.fixed"
        [fixedPosition]="column.fixedPosition"
      ></dxi-column>
    }
  </dx-data-grid>

  <!--
    The table grows in place rather than paging: these rows are a ranking, and turning a page
    would take away the ones the viewer was comparing against.
  -->
  @if (state?.canLoadMore || state?.errorMessage) {
    <div class="comparison-dashboard__mover-load-more">
      @if (state.errorMessage) {
        <span class="comparison-dashboard__mover-load-more-error">{{ state.errorMessage }}</span>
      }
      <dx-button
        [text]="getMoverLoadMoreText(state)"
        stylingMode="text"
        type="default"
        width="100%"
        [disabled]="state.isLoading"
        (onClick)="loadMoreMoverRows(datasetKey, level, direction)"
      ></dx-button>
    </div>
  }
</ng-template>
